import React, { useEffect, useRef, useState } from "react";
import {
  Container, Card, CardBody, Badge, Button, Spinner, Input,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  getPropertyTicketById, updatePropertyTicket, addPropertyTicketMessage,
  onPropertyTicketMessages, notifyPropertyTicketParticipants,
} from "../../helpers/firestoreService";
import { useAuthUser } from "../../hooks/AuthProvider";

const SEVERITY_COLORS = { Low: "info", Medium: "warning", High: "orange", Urgent: "danger" };
const STATUS_COLORS = { Open: "primary", "In Progress": "warning", Resolved: "success", Closed: "secondary" };

const TenantTicketDetail = () => {
  document.title = "Ticket Detail | Porcher";

  const { ticketId } = useParams();
  const navigate = useNavigate();
  const user = useAuthUser();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!ticketId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const t = await getPropertyTicketById(ticketId);
        if (cancelled) return;
        if (!t) { setNotFound(true); return; }
        if (t.createdByUid !== user.uid) { setAccessDenied(true); return; }
        setTicket(t);
      } catch (e) {
        console.error("Load ticket error:", e);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticketId, user]);

  useEffect(() => {
    if (!ticketId) return;
    const unsub = onPropertyTicketMessages(ticketId, setMessages);
    return () => unsub();
  }, [ticketId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sendingMessage) return;
    setSendingMessage(true);
    try {
      await addPropertyTicketMessage(ticketId, {
        senderUid: user.uid,
        senderName: user.displayName || user.email,
        senderEmail: user.email,
        text: newMessage.trim(),
      });
      notifyPropertyTicketParticipants(ticket.propertyId, {
        type: "ticket_message",
        title: "New message on a ticket",
        message: `New message on ticket "${ticket.title}" from ${user.displayName || user.email}.`,
        ticketId,
        excludeUid: user.uid,
      }).catch((err) => console.error("Notify owners error:", err));
      setNewMessage("");
    } catch (e) {
      console.error("Send message error:", e);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleResolve = async () => {
    setResolving(true);
    try {
      await updatePropertyTicket(ticketId, { status: "Resolved" });
      setTicket((prev) => ({ ...prev, status: "Resolved" }));
      notifyPropertyTicketParticipants(ticket.propertyId, {
        type: "ticket_resolved",
        title: "Ticket resolved",
        message: `Ticket "${ticket.title}" has been marked as resolved by the requester.`,
        ticketId,
        excludeUid: user.uid,
      }).catch((err) => console.error("Notify owners error:", err));
    } catch (e) {
      console.error("Resolve ticket error:", e);
    } finally {
      setResolving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (notFound || accessDenied) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5 text-muted">
            <i className="bx bx-error-circle" style={{ fontSize: "2.5rem" }} />
            <p className="mt-2">{notFound ? "Ticket not found." : "You don't have access to this ticket."}</p>
            <Button color="primary" outline size="sm" onClick={() => navigate("/tenant/tickets")}>
              Back to My Tickets
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const canResolve = !["Resolved", "Closed", "Done"].includes(ticket.status);

  const handleCopyLink = () => {
    const url = `${window.location.origin}/ticket/${ticketId}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Button color="link" className="ps-0 text-decoration-none" onClick={() => navigate("/tenant/tickets")}>
            <i className="bx bx-arrow-back me-1" />Back to My Tickets
          </Button>
          <div className="d-flex gap-2">
            <Button color="secondary" outline size="sm" onClick={handleCopyLink}>
              <i className="bx bx-link me-1" />{linkCopied ? "Link copied!" : "Copy public link"}
            </Button>
            {canResolve && (
              <Button color="success" outline size="sm" onClick={handleResolve} disabled={resolving}>
                {resolving ? <Spinner size="sm" /> : <><i className="bx bx-check me-1" />Mark as Resolved</>}
              </Button>
            )}
          </div>
        </div>

        <Card className="border-0 shadow-sm mb-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div>
                <h5 className="mb-1">{ticket.title}</h5>
                <div className="d-flex gap-2 mb-2">
                  <Badge color={SEVERITY_COLORS[ticket.severity] || "secondary"} pill>{ticket.severity || "Low"}</Badge>
                  <Badge color={STATUS_COLORS[ticket.status] || "secondary"} pill>{ticket.status}</Badge>
                </div>
              </div>
              <div className="text-muted small">
                {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : "—"}
              </div>
            </div>
            <p className="mb-0 mt-2">{ticket.description}</p>
          </CardBody>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardBody>
            <h6 className="fw-semibold mb-3 text-muted text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
              <i className="bx bx-chat me-1" />Conversation
            </h6>
            <div style={{ maxHeight: 360, overflowY: "auto" }} className="mb-3">
              {messages.length === 0 ? (
                <p className="text-muted small fst-italic mb-0">No messages yet — say hello!</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`d-flex mb-2 ${m.senderUid === user.uid ? "justify-content-end" : ""}`}>
                    <div
                      className={`p-2 rounded ${m.senderUid === user.uid ? "bg-primary text-white" : "bg-light"}`}
                      style={{ maxWidth: "75%" }}
                    >
                      <div className="small fw-semibold mb-1">{m.senderName}</div>
                      <div className="small">{m.text}</div>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
            <form className="d-flex gap-2" onSubmit={handleSendMessage}>
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message…"
                disabled={sendingMessage}
              />
              <Button color="primary" type="submit" disabled={sendingMessage || !newMessage.trim()}>
                {sendingMessage ? <Spinner size="sm" /> : <i className="bx bx-send" />}
              </Button>
            </form>
            <small className="text-muted d-block mt-2" style={{ fontSize: "0.7rem" }}>
              By using this chat, you agree to our{" "}
              <a href="/community-guidelines" target="_blank" rel="noopener noreferrer">
                Community Guidelines
              </a>.
            </small>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default TenantTicketDetail;
